import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Separator } from '@shared/components/ui/shadcn/separator';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { faImage } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReception } from '../../hooks/useReception';
import { useUpdateReception } from '../../hooks/useUpdateReception';
import { Package, PACKAGE_TYPE_OPTIONS, PackageInput, PackageType, PackageUpdateInput } from '../../models/Reception';
import { LoadingState } from '../feedback/LoadingState';
import { SignatureImageModal } from './SignatureImageModal';

/** URL de la imagen de firma: la API envía la ruta absoluta en signature_ref */
function getSignatureImageUrl(signatureRef: string): string {
  if (!signatureRef?.trim()) return '';
  return signatureRef.trim();
}

interface EditReceptionModalProps {
  receptionId: number;
}

interface PackageFormData extends PackageInput {
  key: string;
  id?: number;
  status?: string;
  isNew?: boolean;
  isDeleted?: boolean;
}

const EditReceptionModal = forwardRef<ModalCenterRef, EditReceptionModalProps>(({ receptionId }, ref) => {
  const { t } = useTranslation('receptionsModule');
  const { toast } = useToast();
  const updateMutation = useUpdateReception();

  const { reception, isLoading: isLoadingReception } = useReception(receptionId);
  const isClosedReception = reception?.status === 'CLOSED';

  // Estado del formulario
  const [carrier, setCarrier] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [driverName, setDriverName] = useState('');
  const [signatureRef, setSignatureRef] = useState('');
  const [packages, setPackages] = useState<PackageFormData[]>([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);

  // Inicializar formulario
  useEffect(() => {
    if (reception) {
      setCarrier(reception.carrier);
      setVehiclePlate(reception.vehicle_plate);
      setDriverName(reception.driver_name);
      setSignatureRef(reception.signature_ref);

      if (reception.packages) {
        setPackages(
          reception.packages.map((pkg: Package) => ({
            key: crypto.randomUUID(),
            id: pkg.id,
            package_type: pkg.package_type,
            package_type_other: pkg.package_type_other,
            quantity: pkg.quantity,
            incident_type: pkg.incident_type,
            image_ref: pkg.image_ref,
            status: pkg.status,
            isNew: false,
            isDeleted: false,
          })),
        );
      }
    }
  }, [reception]);

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
  };

  const handleAddPackage = () => {
    setPackages([
      ...packages,
      {
        key: crypto.randomUUID(),
        package_type: 'BOX',
        quantity: 1,
        isNew: true,
        isDeleted: false,
      },
    ]);
  };

  const handleRemovePackage = (key: string) => {
    const pkg = packages.find((p) => p.key === key);
    if (pkg) {
      if (isClosedReception && !pkg.isNew) {
        return;
      }
      if (pkg.isNew) {
        setPackages(packages.filter((p) => p.key !== key));
      } else if (pkg.status === 'RECEIVED') {
        setPackages(packages.map((p) => (p.key === key ? { ...p, isDeleted: true } : p)));
      } else {
        toast({
          title: t('errors.generic'),
          description: t('errors.package_already_identified'),
          variant: 'destructive',
        });
      }
    }
  };

  const handleRestorePackage = (key: string) => {
    const pkg = packages.find((p) => p.key === key);
    if (isClosedReception && pkg && !pkg.isNew) {
      return;
    }
    setPackages(packages.map((p) => (p.key === key ? { ...p, isDeleted: false } : p)));
  };

  const handlePackageChange = (key: string, field: keyof PackageFormData, value: any) => {
    const pkg = packages.find((p) => p.key === key);
    if (isClosedReception && pkg && !pkg.isNew) {
      return;
    }
    if (pkg && !pkg.isNew && pkg.status === 'IDENTIFIED') {
      toast({
        title: t('errors.generic'),
        description: t('errors.package_already_identified'),
        variant: 'destructive',
      });
      return;
    }
    setPackages(packages.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
  };

  const handleUpdate = async () => {
    try {
      if (!reception) return;

      const packagesToAdd: PackageInput[] = packages
        .filter((p) => p.isNew && !p.isDeleted)
        .map((pkg) => ({
          package_type: pkg.package_type,
          package_type_other: pkg.package_type === 'OTHER' ? pkg.package_type_other : null,
          quantity: pkg.quantity,
          incident_type: pkg.incident_type || null,
          image_ref: pkg.image_ref || null,
        }));

      const packagesToUpdate: PackageUpdateInput[] = packages
        .filter((p) => !isClosedReception && !p.isNew && !p.isDeleted && p.status === 'RECEIVED' && p.id)
        .map((pkg) => ({
          id: pkg.id!,
          package_type: pkg.package_type,
          package_type_other: pkg.package_type === 'OTHER' ? pkg.package_type_other : null,
          quantity: pkg.quantity,
          incident_type: pkg.incident_type || null,
          image_ref: pkg.image_ref || null,
        }));

      const packageIdsToDelete: number[] = packages
        .filter((p) => !isClosedReception && p.isDeleted && p.id)
        .map((p) => p.id!);

      await updateMutation.mutateAsync({
        id: receptionId,
        data: {
          carrier: carrier.trim(),
          vehicle_plate: vehiclePlate.trim(),
          driver_name: driverName.trim(),
          signature_ref: signatureRef.trim(),
          packages_to_add: packagesToAdd.length > 0 ? packagesToAdd : undefined,
          packages_to_update: packagesToUpdate.length > 0 ? packagesToUpdate : undefined,
          package_ids_to_delete: packageIdsToDelete.length > 0 ? packageIdsToDelete : undefined,
        },
        currentStatus: reception.status,
      });

      toast({
        title: t('modal.edit.success_title'),
        description: t('modal.edit.success_description'),
      });

      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.edit.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = updateMutation.isPending || isLoadingReception;

  if (isLoadingReception) {
    return (
      <ModalCenter ref={ref} title={t('modal.edit.title')} type="default" width="xl">
        <LoadingState className="h-32" />
      </ModalCenter>
    );
  }

  const visiblePackages = packages.filter((p) => !p.isDeleted);
  const deletedPackages = packages.filter((p) => p.isDeleted);

  return (
    <ModalCenter ref={ref} title={t('modal.edit.title')} type="default" width="xl">
      <div className="space-y-6">
        {/* Sección: Datos del transporte */}
        <div>
          <h3 className="text-sm font-medium text-complementary-text mb-4">{t('modal.view.reception_info')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-complementary-text mb-1 block">{t('table.carrier')}</label>
              <Input
                placeholder={t('modal.create.carrier_placeholder')}
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                disabled={isLoading || isClosedReception}
              />
            </div>
            <div>
              <label className="text-xs text-complementary-text mb-1 block">{t('table.vehicle_plate')}</label>
              <Input
                placeholder={t('modal.create.vehicle_plate_placeholder')}
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value)}
                disabled={isLoading || isClosedReception}
              />
            </div>
            <div>
              <label className="text-xs text-complementary-text mb-1 block">{t('table.driver_name')}</label>
              <Input
                placeholder={t('modal.create.driver_name_placeholder')}
                value={driverName}
                onChange={(e) => setDriverName(e.target.value)}
                disabled={isLoading || isClosedReception}
              />
            </div>
            <div>
              <label className="text-xs text-complementary-text mb-1 block">
                {t('modal.create.signature_ref_label')}
              </label>
              <div className="space-y-2">
                <div
                  className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20 cursor-pointer hover:bg-muted/30 transition-colors"
                  onClick={() => signatureRef.trim() && setShowSignatureModal(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && signatureRef.trim()) {
                      setShowSignatureModal(true);
                    }
                  }}
                  data-testid="view-signature-button"
                >
                  <div className="w-14 h-10 rounded border border-border bg-body-bg flex items-center justify-center overflow-hidden shrink-0">
                    {signatureRef.trim() ? (
                      <img
                        src={getSignatureImageUrl(signatureRef)}
                        alt=""
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <FontAwesomeIcon icon={faImage} className="w-5 h-5 text-icons" />
                    )}
                  </div>
                  <span className="text-sm text-foreground">{t('modal.view.view_signature')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Sección: Paquetes */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-complementary-text">
              {t('modal.create.packages_title')} ({visiblePackages.length})
            </h3>
            <Button variant="outline" size="sm" onClick={handleAddPackage} disabled={isLoading}>
              {t('button.add_package')}
            </Button>
          </div>

          {/* Tabla de paquetes */}
          <div className="border rounded-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/50 text-xs font-medium text-complementary-text">
              <div className="col-span-1">#</div>
              <div className="col-span-4">{t('modal.create.package_type_label')}</div>
              <div className="col-span-2 text-center">{t('modal.create.quantity_label')}</div>
              <div className="col-span-3">{t('table.status')}</div>
              <div className="col-span-2"></div>
            </div>

            {/* Filas */}
            <div className="divide-y divide-border max-h-[200px] overflow-auto">
              {visiblePackages.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-complementary-text">
                  {t('modal.view.no_packages')}
                </div>
              ) : (
                visiblePackages.map((pkg, index) => {
                  const isEditable = isClosedReception ? !!pkg.isNew : pkg.isNew || pkg.status === 'RECEIVED';
                  return (
                    <div
                      key={pkg.key}
                      className={`grid grid-cols-12 gap-2 px-3 py-2 items-center ${
                        pkg.status === 'IDENTIFIED' ? 'bg-success/5' : ''
                      }`}
                    >
                      <div className="col-span-1 text-sm text-complementary-text">{index + 1}</div>
                      <div className="col-span-4">
                        <Select
                          value={pkg.package_type}
                          onValueChange={(value) => handlePackageChange(pkg.key, 'package_type', value as PackageType)}
                          disabled={isLoading || !isEditable}
                        >
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PACKAGE_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {t(option.labelKey)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min={1}
                          value={pkg.quantity}
                          onClick={(e) => e.currentTarget.select()}
                          onFocus={(e) => e.currentTarget.select()}
                          onChange={(e) => {
                            const rawValue = e.target.value;
                            if (rawValue === '') {
                              handlePackageChange(pkg.key, 'quantity', 0);
                              return;
                            }

                            const nextQuantity = Number(rawValue);
                            if (!Number.isNaN(nextQuantity)) {
                              handlePackageChange(pkg.key, 'quantity', nextQuantity);
                            }
                          }}
                          onBlur={() => {
                            if (!pkg.quantity || pkg.quantity < 1) {
                              handlePackageChange(pkg.key, 'quantity', 1);
                            }
                          }}
                          disabled={isLoading || !isEditable}
                          className="h-8 text-sm text-center"
                        />
                      </div>
                      <div className="col-span-3">
                        {pkg.isNew ? (
                          <span className="text-xs text-primary">Nuevo</span>
                        ) : pkg.status === 'IDENTIFIED' ? (
                          <span className="text-xs text-success">Identificado</span>
                        ) : (
                          <span className="text-xs text-complementary-text">Recibido</span>
                        )}
                      </div>
                      <div className="col-span-2 text-right">
                        {isEditable && (
                          <button
                            onClick={() => handleRemovePackage(pkg.key)}
                            disabled={isLoading}
                            className="text-xs text-destructive hover:underline"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Paquetes a eliminar */}
          {deletedPackages.length > 0 && (
            <div className="mt-3 p-3 bg-destructive/5 rounded-lg">
              <p className="text-xs text-destructive mb-2">Se eliminarán {deletedPackages.length} paquete(s):</p>
              <div className="space-y-1">
                {deletedPackages.map((pkg) => (
                  <div key={pkg.key} className="flex items-center justify-between text-sm">
                    <span className="text-complementary-text line-through">
                      {t(`package_types.${pkg.package_type.toLowerCase()}`)} × {pkg.quantity}
                    </span>
                    <button
                      onClick={() => handleRestorePackage(pkg.key)}
                      disabled={isLoading}
                      className="text-xs text-primary hover:underline"
                    >
                      Restaurar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Botones */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" onClick={handleUpdate} disabled={isLoading}>
            {isLoading ? t('button.updating') : t('button.update')}
          </Button>
        </div>

        {/* Modal para ver firma en grande */}
        <SignatureImageModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          imageUrl={getSignatureImageUrl(signatureRef)}
          data-testid="signature-image-modal"
        />
      </div>
    </ModalCenter>
  );
});

EditReceptionModal.displayName = 'EditReceptionModal';

export default EditReceptionModal;
